import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@roger/ui-components';
import { Loader2 } from 'lucide-react';
import {
  decideOrderFaxSignature,
  fetchReturnedFaxPage,
  getReturnedFaxPageCount,
} from '../../services/orderFaxes';

/**
 * The page that came back, so somebody can answer for it without leaving the
 * Order Manager.
 *
 * It exists because most people who chase physician signatures cannot see the
 * Fax Inbox: of the eleven roles that reach the Order Manager, only five hold
 * the fax permission, and the ones that do not are exactly the chart and
 * clinical roles doing the chasing. Sending them to a screen they cannot open
 * would be worse than showing nothing.
 *
 * The pages arrive as images rendered on the server rather than as the fax
 * itself. A fax comes from a sender nobody vetted, and the inbox only ever
 * serves these as downloads so that whatever arrived cannot run in a reader's
 * browser. A picture of the page cannot execute anything, and looking at it is
 * the whole purpose.
 */
interface ReturnedFaxDialogProps {
  /** The order whose returned page is being reviewed. Null closes it. */
  orderId: string | null;
  onClose: () => void;
  onAnswered: () => void;
  onError: (message: string) => void;
}

const ReturnedFaxDialog: React.FC<ReturnedFaxDialogProps> = ({
  orderId,
  onClose,
  onAnswered,
  onError,
}) => {
  const [pages, setPages] = useState<string[] | null>(null);
  const [deciding, setDeciding] = useState(false);

  useEffect(() => {
    if (!orderId) {
      setPages(null);
      return;
    }
    let cancelled = false;
    let made: string[] = [];
    void (async () => {
      try {
        const count = await getReturnedFaxPageCount(orderId);
        made = await Promise.all(
          Array.from({ length: count }, (_, index) =>
            fetchReturnedFaxPage(orderId, index)
          )
        );
        if (cancelled) {
          made.forEach(URL.revokeObjectURL);
          return;
        }
        setPages(made);
      } catch {
        if (!cancelled) {
          onError('Could not open the returned fax.');
          onClose();
        }
      }
    })();
    return () => {
      cancelled = true;
      // Blob URLs are held by the document until revoked, so a dialog opened
      // and closed repeatedly would otherwise keep every page it ever drew.
      made.forEach(URL.revokeObjectURL);
    };
  }, [orderId, onClose, onError]);

  const answer = useCallback(
    async (signed: boolean) => {
      if (!orderId) return;
      setDeciding(true);
      try {
        await decideOrderFaxSignature(orderId, signed);
        onAnswered();
        onClose();
      } catch {
        onError('Could not record that answer.');
      } finally {
        setDeciding(false);
      }
    },
    [orderId, onAnswered, onClose, onError]
  );

  return (
    <Dialog open={orderId !== null} onOpenChange={open => !open && onClose()}>
      <DialogContent className='max-w-3xl'>
        <DialogHeader>
          <DialogTitle>Returned fax</DialogTitle>
          <DialogDescription>
            This page came back carrying this order&rsquo;s mark. The mark says
            which order it is and nothing about whether it was signed.
          </DialogDescription>
        </DialogHeader>

        <div className='max-h-[60vh] overflow-auto rounded border bg-muted/30 p-2'>
          {pages === null ? (
            <div className='flex items-center justify-center p-8 text-sm text-muted-foreground'>
              <Loader2 className='mr-2 h-4 w-4 animate-spin' />
              Loading the page
            </div>
          ) : (
            pages.map((src, index) => (
              <img
                key={src}
                src={src}
                alt={`Returned fax page ${index + 1} of ${pages.length}`}
                className='mx-auto mb-2 w-full max-w-2xl border bg-white shadow-sm'
              />
            ))
          )}
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={onClose} disabled={deciding}>
            Cancel
          </Button>
          <Button
            variant='outline'
            onClick={() => void answer(false)}
            disabled={deciding || pages === null}
          >
            Not signed
          </Button>
          <Button
            onClick={() => void answer(true)}
            disabled={deciding || pages === null}
          >
            {deciding && <Loader2 className='mr-1.5 h-4 w-4 animate-spin' />}
            Confirm signed
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReturnedFaxDialog;
